/**
 * AA Health Projects is the single source of truth for the application's visual language.
 *
 * Tailwind consumes these values directly. `npm run design:tokens` generates
 * the matching CSS custom properties used by shared component classes.
 */
export const designTokens = Object.freeze({
  name: 'AA Health Projects',
  colors: {
    primary: '#4F6343',
    primaryDeep: '#1B3A2D',
    primaryPress: '#1B3A2D',
    primarySoft: '#D5DCD0',
    primarySubdued: '#EAEEE8',
    strong: '#1B3A2D',
    brandDark: '#1B3A2D',
    info: '#4B618A',
    warning: '#85602A',
    danger: '#9B522E',
    ink: '#3B3D38',
    inkSecondary: '#595B54',
    inkMute: '#595B54',
    inkMute2: '#595B54',
    canvas: '#FFFFFF',
    canvasGrouped: '#F2F1EF',
    canvasSoft: '#F2F1EF',
    canvasCream: '#EAEEE8',
    hairline: '#DDD1C4',
    hairlineSoft: '#EAEEE8',
    hairlineInput: '#828282',
    fillTertiary: '#EAEEE8',
    fillDisabled: '#D5DCD0',
    controlStroke: '#595B54',
    sheetBackdrop: 'rgba(27, 58, 45, 0.32)',
    shadow: '#1B3A2D',
  },
  typography: {
    sans: "'Manrope Variable', Manrope, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    display: "'Instrument Serif', Georgia, serif",
    mono: "'SFMono-Regular', Consolas, monospace",
    weights: { display: '400', body: '400', action: '600', emphasis: '600' },
  },
  spacing: {
    xxs: '2px',
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
    huge: '64px',
  },
  typeScale: {
    displayLgSize: '32px',
    displayLgLeading: '1.25',
    displayLgTracking: '0px',
    displayMdSize: '24px',
    displayMdLeading: '1.333333',
    displayMdTracking: '0px',
    headingLgSize: '24px',
    headingLgLeading: '1.333333',
    headingLgTracking: '0px',
    headingSmSize: '18px',
    headingSmLeading: '1.444444',
    bodyLgSize: '15px',
    bodyLgLeading: '1.533333',
    bodyMdSize: '15px',
    bodyMdLeading: '1.533333',
    bodyTabularSize: '14px',
    bodyTabularLeading: '1.428571',
    bodyTabularTracking: '0px',
    captionSize: '13px',
    captionLeading: '1.538462',
    captionTracking: '0px',
    microSize: '12px',
    microLeading: '1.5',
    microCapSize: '12px',
    microCapLeading: '1.5',
    microCapTracking: '0px',
  },
  layout: {
    sidebarExpanded: '248px',
    sidebarCollapsed: '60px',
    topbarHeight: '64px',
    taskPanelWidth: '480px',
    contentMax: '1200px',
  },
  radii: {
    xs: '8px',
    sm: '8px',
    md: '12px',
    lg: '12px',
    xl: '16px',
    xxl: '24px',
    xxxl: '32px',
    pill: '9999px',
  },
  shadows: {
    level1: 'none',
    level2: '0 4px 16px rgba(27,58,45,0.08)',
    level3: '0 12px 32px rgba(27,58,45,0.12)',
    upward: 'none',
    inset: 'inset 0 0 0 1px rgba(27,58,45,0.08)',
    focus: '0 0 0 3px rgba(79,99,67,0.2)',
  },
  motion: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    ease: 'cubic-bezier(0.2, 0, 0, 1)',
  },
});

const { colors } = designTokens;

export const neutralScale = Object.freeze({
  50: colors.canvasSoft,
  100: colors.canvasCream,
  200: colors.hairline,
  300: colors.hairlineInput,
  400: colors.inkMute2,
  500: colors.inkMute,
  600: colors.inkSecondary,
  700: colors.strong,
  800: colors.brandDark,
  900: colors.ink,
  950: colors.ink,
});

export const primaryScale = Object.freeze({
  50: colors.canvasSoft,
  100: colors.canvasCream,
  200: colors.primarySubdued,
  300: colors.primarySoft,
  400: colors.primarySoft,
  500: colors.primary,
  600: colors.primary,
  700: colors.strong,
  800: colors.primaryPress,
  900: colors.strong,
  950: colors.brandDark,
});

export const statusScales = Object.freeze({
  ok: {
    50: colors.canvasSoft,
    100: colors.primarySubdued,
    200: colors.primarySubdued,
    300: colors.primarySoft,
    400: colors.primary,
    500: colors.primary,
    600: colors.primary,
    700: colors.primaryPress,
    800: colors.strong,
    900: colors.brandDark,
  },
  pending: {
    50: colors.canvasSoft,
    100: colors.primarySubdued,
    200: colors.hairline,
    300: colors.primarySoft,
    400: colors.warning,
    500: colors.warning,
    600: colors.warning,
    700: colors.warning,
    800: colors.strong,
    900: colors.strong,
  },
  failed: {
    50: colors.canvasSoft,
    100: colors.primarySubdued,
    200: colors.hairline,
    300: colors.primarySoft,
    400: colors.danger,
    500: colors.danger,
    600: colors.danger,
    700: colors.danger,
    800: colors.strong,
    900: colors.strong,
  },
  info: {
    50: colors.canvasSoft,
    100: colors.primarySubdued,
    200: colors.hairline,
    300: colors.primarySoft,
    400: colors.info,
    500: colors.info,
    600: colors.info,
    700: colors.info,
    800: colors.strong,
    900: colors.strong,
  },
});
